import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { getBackendUrl } from '../../lib/env';

const API_URL = getBackendUrl();

function money(currency, value) {
  return `${currency} ${Number(value || 0).toLocaleString()}`;
}

const METHOD_LABELS = {
  online_payment: 'Pay online with Jjuma Pay',
  whatsapp_order: 'Order via WhatsApp',
  cash_on_delivery: 'Cash on delivery',
};

const UGANDA_PHONE_REGEX = /^(\+?256)(70|77)\d{7}$/;

export default function StoreCheckout(props = {}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const username = props.resolvedUsername || params.username || '';
  const productSlug = props.resolvedProductSlug || searchParams.get('product') || searchParams.get('product_slug') || '';
  const pathPrefix = props.publicPathPrefix !== undefined ? props.publicPathPrefix : (username ? `/${username}` : '');
  const orderRef = searchParams.get('order') || '';
  const [data, setData] = useState(null);
  const [orderState, setOrderState] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    fulfillment_type: 'delivery',
    payment_method: 'online_payment',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/api/public/stores/${username}/checkout`, { params: productSlug ? { product_slug: productSlug } : {} })
      .then(r => {
        setData(r.data);
        const methods = r.data.allowed_methods || [];
        const storeData = r.data.store || {};
        const defaultFulfillment = storeData.delivery_enabled !== false ? 'delivery' : (storeData.pickup_enabled !== false ? 'pickup' : 'delivery');
        setForm(prev => ({
          ...prev,
          payment_method: methods.includes('online_payment') ? 'online_payment' : (methods[0] || 'online_payment'),
          fulfillment_type: defaultFulfillment,
        }));
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [username, productSlug]);

  useEffect(() => {
    if (!orderRef) return;
    axios.get(`${API_URL}/api/public/stores/${username}/orders/${orderRef}`)
      .then(r => setOrderState(r.data.order || null))
      .catch(() => setOrderState(null));
  }, [username, orderRef]);

  const store = data?.store || null;
  const product = data?.product || null;
  const allowedMethods = data?.allowed_methods || [];
  const fulfillmentOptions = [
    store?.delivery_enabled !== false ? { value: 'delivery', label: 'Delivery' } : null,
    store?.pickup_enabled !== false ? { value: 'pickup', label: 'Pickup' } : null,
  ].filter(Boolean);
  const selectedFulfillment = form.fulfillment_type || fulfillmentOptions[0]?.value || 'delivery';
  const basePrice = Number(product?.sale_price || product?.price || 0);
  const deliveryFee = selectedFulfillment === 'delivery' ? Number(store?.delivery_fee || 0) : 0;
  const totalEstimate = basePrice + deliveryFee;
  const isReady = !!store && !!product;
  const selectedMethod = form.payment_method;
  const selectedMethodLabel = METHOD_LABELS[selectedMethod] || selectedMethod;
  const phoneValid = useMemo(() => UGANDA_PHONE_REGEX.test(String(form.phone || '').replace(/\s+/g, '')), [form.phone]);
  const buildPath = (suffix = '') => {
    const base = pathPrefix || '';
    if (!suffix) return base || '/';
    const cleanSuffix = suffix.startsWith('/') ? suffix : `/${suffix}`;
    return `${base}${cleanSuffix}` || '/';
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.customer_name.trim()) {
      setError('Customer name is required.');
      return;
    }
    if (!UGANDA_PHONE_REGEX.test(String(form.phone || '').replace(/\s+/g, ''))) {
      setError('Enter a valid Uganda phone number like 2567XXXXXXXX.');
      return;
    }
    if (!fulfillmentOptions.some(option => option.value === form.fulfillment_type)) {
      setError('Selected fulfillment option is not available for this store.');
      return;
    }
    if (!allowedMethods.includes(form.payment_method)) {
      setError('Selected payment method is not available for this store.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        product_slug: product.slug,
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        address: form.address,
        notes: form.notes,
        fulfillment_type: form.fulfillment_type,
        pickup_location: store.pickup_location || '',
        delivery_notes: store.delivery_notes || '',
        payment_method: form.payment_method,
      };
      const r = await axios.post(`${API_URL}/api/public/stores/${username}/checkout`, payload);
      const response = r.data || {};
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return;
      }
      if (response.whatsapp_url) {
        window.location.href = response.whatsapp_url;
        return;
      }
      if (response.order) {
        setOrderState(response.order);
      }
      setError(response.message || 'Order created successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not complete checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-xl bg-white border border-slate-200 rounded-[28px] shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-slate-500">Checkout not available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)]">
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.24em] text-emerald-700 font-semibold">Store checkout</p>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {store.store_name}
          </h1>
          <p className="text-slate-500 text-sm mt-1">Complete your order in a few steps.</p>
        </div>

        {orderState ? (
          <Card className="mb-5 border border-emerald-200 bg-emerald-50/70 shadow-sm rounded-[24px]">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-emerald-900">Order status: {orderState.status}</p>
              <p className="text-sm text-emerald-900/80 mt-1">{orderState.gateway_message || 'Your order has been saved.'}</p>
              <p className="text-xs text-emerald-800 mt-2">Reference: {orderState.reference_code || orderState.transaction_reference}</p>
            </CardContent>
          </Card>
        ) : null}

        {error ? (
          <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <Card className="bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px] overflow-hidden">
            <CardContent className="p-5 sm:p-6">
              <form onSubmit={submit} className="grid gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Customer name</label>
                    <Input value={form.customer_name} onChange={e => update('customer_name', e.target.value)} className="mt-1 h-12" placeholder="Your name" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Phone number</label>
                    <Input value={form.phone} onChange={e => update('phone', e.target.value)} className="mt-1 h-12" placeholder="25677XXXXXXX" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Email</label>
                    <Input value={form.email} onChange={e => update('email', e.target.value)} className="mt-1 h-12" placeholder="name@example.com" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">{selectedFulfillment === 'pickup' ? 'Pickup notes' : 'Delivery address'}</label>
                    <Input value={form.address} onChange={e => update('address', e.target.value)} className="mt-1 h-12" placeholder={selectedFulfillment === 'pickup' ? 'Pickup note or time' : 'Delivery address'} />
                  </div>
                </div>
                {fulfillmentOptions.length > 0 ? (
                  <div className="space-y-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Fulfillment</h2>
                      <p className="text-sm text-slate-500">Choose how you want to receive the order.</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {fulfillmentOptions.map((option) => {
                        const active = selectedFulfillment === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => update('fulfillment_type', option.value)}
                            className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                              active
                                ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-[0_8px_24px_rgba(16,185,129,0.12)]'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50/40'
                            }`}
                          >
                            <p className="text-sm font-medium">{option.label}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {option.value === 'delivery'
                                ? `Delivery fee ${money(store.currency, store.delivery_fee || 0)}`
                                : (store.pickup_location ? `Pickup at ${store.pickup_location}` : 'Pickup from the store')}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className="text-sm font-medium text-slate-700">Notes</label>
                  <Textarea value={form.notes} onChange={e => update('notes', e.target.value)} className="mt-1 min-h-[110px]" placeholder="Optional order notes" />
                </div>

                <div className="space-y-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment method</h2>
                    <p className="text-sm text-slate-500">Choose how you want to complete the order.</p>
                  </div>
                  <div className="grid gap-3">
                    {allowedMethods.map((method) => {
                      const active = selectedMethod === method;
                      return (
                        <button
                          key={method}
                          type="button"
                          onClick={() => update('payment_method', method)}
                          className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                            active
                              ? 'border-emerald-600 bg-emerald-50 text-emerald-800 shadow-[0_8px_24px_rgba(16,185,129,0.12)]'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-200 hover:bg-emerald-50/40'
                          }`}
                        >
                          <p className="text-sm font-medium">{METHOD_LABELS[method] || method}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {method === 'online_payment' ? 'Uses the enabled Jjuma Pay gateway automatically.' : method === 'whatsapp_order' ? 'Creates a WhatsApp message for the store owner.' : 'Saves the order for delivery on arrival.'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                  {!phoneValid ? (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-2xl p-3">
                      Use a valid Uganda number like 2567XXXXXXXX.
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <Button type="submit" disabled={submitting || !allowedMethods.length} className="bg-emerald-700 hover:bg-emerald-800 text-white h-12">
                    {submitting ? 'Processing...' : (selectedMethodLabel || 'Continue')}
                  </Button>
                  <Button type="button" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-12" onClick={() => window.location.href = buildPath()}>Back to store</Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200 shadow-[0_18px_50px_rgba(15,23,42,0.08)] rounded-[28px] overflow-hidden h-fit">
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="rounded-[24px] overflow-hidden border border-slate-100 bg-slate-50">
                <div className="aspect-[4/3] bg-slate-100">
                  {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover" /> : null}
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{product.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{product.short_description}</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Price</span>
                  <span className="text-sm font-medium text-slate-900">{money(product.currency, product.sale_price || product.price)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Fulfillment</span>
                  <span className="text-sm font-medium text-slate-900">{selectedFulfillment === 'pickup' ? 'Pickup' : 'Delivery'}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Delivery fee</span>
                  <span className="text-sm font-medium text-slate-900">{money(product.currency, deliveryFee)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Total</span>
                  <span className="text-sm font-medium text-slate-900">{money(product.currency, totalEstimate)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm text-slate-500">Method</span>
                  <span className="text-sm font-medium text-slate-900">{selectedMethodLabel}</span>
                </div>
              </div>
              {selectedFulfillment === 'pickup' && store.pickup_location ? (
                <p className="text-xs text-slate-500">Pickup location: {store.pickup_location}</p>
              ) : null}
              <p className="text-xs text-slate-500">
                {allowedMethods.length ? `Available methods: ${allowedMethods.map(method => METHOD_LABELS[method] || method).join(' • ')}` : 'No checkout methods are available for this store.'}
              </p>
            </CardContent>
          </Card>
        </div>

        {!store.remove_branding ? (
          <p className="text-center text-xs text-slate-500 py-6">Powered by Jjuma Pay</p>
        ) : null}
      </div>
    </div>
  );
}
